import type { LogicalProfile } from '@mfw/common';

import { omitDeepUndefined } from '@mfw/components';
import { OrganizationUserType } from '@mfw/common';

import type {
  ArchiveUnarchiveOrganizationUserArgs,
  ConfirmUserByVerificationCodeArgs,
  FetchOrgUserSignedProfileUrlArgs,
  FetchOrganizationUsersByRoleArgs,
  DisconnectOrganizationUserArgs,
  CheckIfUserExistsOnInviteArgs,
  FetchPublicHealthIssuesArgs,
  FetchCurrentUserByTenantId,
  FetchForgottenPasswordArgs,
  ValidateOnboardingStepArgs,
  FetchOrganizationUsersArgs,
  DeleteOrganizationUserArgs,
  CreateClientOnboardingArgs,
  ChangeCorpPasswordArgs,
  RegisterAccountResult,
  AcceptUserInviteArgs,
  UpdateUserRolesArgs,
  FetchCountriesArgs,
  CreateProfileArgs,
  UpdateOrgUserArgs,
  FetchUserByIdArgs,
  ResetPasswordArgs,
  UiUserUpdateArgs,
  RegisterUserArgs,
  FetchAuthorsArgs,
  FetchUsersArgs,
} from './types/user.types';
import type {
  FetchSignedUploadUrlArgs,
  ApiSignedUploadUrl,
  UiSignedUploadUrl,
} from './types/files.types';
import type {
  ApiOrganization,
  UiOrganization,
} from '../schemas/organizations.schema';
import type {
  ApiAddressCountry,
  UiAddressCountry,
} from '../schemas/address.schema';
import type {
  BaseApiPaginatedResponse,
  BaseApiResponse,
} from './types/api.types';
import type {
  ApiInviteCheck,
  ApiAuthUser,
  ApiAuthor,
  ApiUser,
} from '../schemas/users.schema';
import type { ApiTag, UiTag } from '../schemas/tags.schema';
import type { Maybe } from '../types/maybe.types';

import {
  createUpdateUserArgs,
  mapAuthUserFromApi,
  mapAuthorFromApi,
  mapUserFromApi,
} from './mappers/users.mappers';
import {
  postWithPagination,
  sessionCookie,
  destroy,
  patch,
  post,
  get,
  put,
} from './axios';
import { mapOrganizationFromApi } from './mappers/organizations.mappers';
import { mergeDefaultSocialMedia } from './mappers/socialMedia.mappers';
import { mapAddressCountryFromApi } from './mappers/address.mappers';
import { mapTagFromApi } from './mappers/tags.mappers';

/**
 * The app's ONLY `/auth/me` caller, and it goes through the session guard rather than
 * issuing the request itself. That is what lets one renewal serve everyone: a wake-up
 * trigger, a 403 recovery and this call all share a single flight, so `onIdTokenChanged`
 * can no longer race a request barrier into two simultaneous `/auth/me` round trips.
 *
 * It rejects when a sign-out or a tenant switch lands mid-renewal — by design. The answer
 * would describe a session the app has already left.
 */
export async function fetchCurrentUser({
  afterMutation = false,
  token,
}: FetchCurrentUserByTenantId) {
  const response = await sessionCookie.renew(token, { afterMutation });

  return mapAuthUserFromApi(response?.data);
}

// Home/Calendar/Org Home/Schedule F4.1 — the account/org switchers' data
// source. Already a flat, camelCase shape shared via @mfw/common — no
// mapper needed, unlike most other entities here.
const LOGOUT_REQUEST_TIMEOUT_MS = 5_000;

/**
 * Ends the session on the server, which is what actually clears the `auth` cookie.
 *
 * Sign-out was Firebase-only until now, and the cookie is the entire server-side credential
 * — so it outlived every "log out" and kept authenticating whoever reopened the app on that
 * browser until its own deadline.
 *
 * `skipSessionRenewal` is load-bearing: this request must go out on the cookie it is about
 * to delete. Through the barrier it would instead consult a guard that is about to be reset
 * — and could renew that cookie one last time, or refuse the request outright while a
 * renewal cooldown is active.
 *
 * `_retry` is its response-side twin. The barrier flag silences only the REQUEST
 * interceptor; a 401 or a coded 403 answered to this call would still be treated as
 * recoverable, and the recovery re-mints the cookie through `/auth/me` in the middle of
 * the sign-out — BEFORE `sessionCookie.reset()` runs. The route is unguarded today, so the
 * path is dormant; the flag is what keeps it that way if that ever changes.
 *
 * The deadline is load-bearing too. Axios defaults to `timeout: 0` — wait forever — and
 * `signOutAsync` AWAITS this call before it touches anything local, so a connection that
 * stalls rather than fails would strand the user in a session they asked to leave, with
 * the retry deduped into the same dead promise. Five seconds is deliberately shorter than
 * any cookie lifetime: the server-side deletion is worth a bounded attempt, never an
 * indefinitely stranded local session.
 */
export async function endSession() {
  await post('/v2/auth/logout', undefined, {
    _retry: true,
    skipSessionRenewal: true,
    timeout: LOGOUT_REQUEST_TIMEOUT_MS,
  });
}

export async function fetchLogicalProfiles() {
  const response = await get<BaseApiResponse<LogicalProfile[]>>(
    '/v2/auth/me/logical-profiles',
  );
  return response?.data ?? [];
}

export const fetchUserSignedProfileUrl = async ({
  mediaType,
}: FetchSignedUploadUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2/auth/signed-upload-profile-url?mediaType=${mediaType}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export async function fetchUserById({ id, organizationId }: FetchUserByIdArgs) {
  const response = await get<BaseApiResponse<ApiUser>>(
    `/v2/${organizationId ? `organizations/${organizationId}/clients` : 'users'}/${id}`,
  );
  return mapUserFromApi(response?.data);
}

type CreateAccountResponseData = {
  reactivatedOrganizationIds?: string[];
  needsReactivationChoice?: boolean;
  needsVerification?: boolean;
  reactivated?: boolean;
  user?: ApiUser;
  email?: string;
};

export async function registerUserWithPassword({
  confirmPassword,
  email,
  name,
  password,
  privacyPolicy,
  reactivate,
  username,
}: RegisterUserArgs): Promise<RegisterAccountResult> {
  const response = await post<CreateAccountResponseData>(
    `/v2/auth/create-account`,
    {
      confirmPassword,
      email,
      name,
      password,
      privacyPolicy,
      reactivate,
      username,
    },
  );
  const data = response?.data;

  // The email belongs to a previously deleted account and no decision was made.
  if (data?.needsReactivationChoice) {
    return { email: data.email ?? email, kind: 'needsReactivationChoice' };
  }

  if (data?.reactivated) {
    return {
      kind: 'reactivated',
      reactivatedOrganizationIds: data.reactivatedOrganizationIds ?? [],
      user: mapAuthUserFromApi(data as ApiAuthUser),
    };
  }

  return { kind: 'created', user: mapAuthUserFromApi(data as ApiAuthUser) };
}

export type UsernameAvailability = {
  suggestions: string[];
  available: boolean;
  username: string;
};

export async function fetchUsernameAvailability(username: string) {
  const response = await get<BaseApiResponse<UsernameAvailability>>(
    '/v2/auth/username-availability',
    { query: { username } },
  );
  return response?.data;
}

export async function changeCorpPassword({
  confirmPassword,
  newPassword,
}: ChangeCorpPasswordArgs) {
  // PR-2 §4.9: the write that revokes the caller's own session; it passes the transition
  // hold at the barrier and, if the cookie is missing, gets the one normal recovery.
  return patch(
    `/v2/auth/me/corp-password`,
    { confirmPassword, newPassword },
    { credentialTransition: true },
  );
}

export async function fetchForgottenPassword({
  email,
}: FetchForgottenPasswordArgs) {
  return get<void>(
    `/v2/auth/forgot-password?email=${encodeURIComponent(email)}`,
  );
}

export async function resetPassword({ password, token }: ResetPasswordArgs) {
  return patch(`/v2/auth/reset-password?token=${token}`, { password });
}

export async function validateOnboardingStep({
  address,
  ...args
}: ValidateOnboardingStepArgs) {
  return post<BaseApiResponse<void>>(`/v2/auth/me/onboarding/validate`, {
    ...args,
    // The endpoint validates `address` with the organization DTO (city/zip/
    // country required), so callers that don't need the address checked —
    // client onboarding, where those fields are optional — must have the key
    // omitted entirely; an empty object would still be validated.
    ...(address
      ? { address: { ...address, country: address.country?.[0]?.value } }
      : {}),
  });
}

// F4: `member` onboarding carries no payload beyond the type — the backend
// DTO is `.strict()` with only `type`, so this bypasses createOnboardingUser
// (which always spreads address/socialMedia keys onto the body).
export async function createMemberOnboarding() {
  await post<BaseApiResponse<void>>(`/v2/auth/me/onboarding`, {
    type: 'member',
  });
}

export async function createOnboardingUser(args: CreateClientOnboardingArgs) {
  const allArgs = { ...args };
  if (args.type === 'trainer') {
    delete allArgs.email;
  }
  await post<BaseApiResponse<void>>(`/v2/auth/me/onboarding`, {
    ...allArgs,
    address: {
      ...allArgs.address,
      country: allArgs.address?.country?.[0]?.value,
    },
    socialMedia: allArgs.socialMedia
      ? mergeDefaultSocialMedia(allArgs.socialMedia)
      : [],
  });
}

// F7 — "Add profile": same payload shape/mapping as createOnboardingUser,
// hitting a different endpoint (doesn't touch onboardingCompletedAt, any
// account type may call it any number of times). Returns the created org so
// the caller can select it into context.
export async function createProfile(
  args: CreateProfileArgs,
): Promise<Maybe<UiOrganization>> {
  const allArgs = { ...args };
  if (args.type === 'trainer') {
    delete allArgs.email;
  }
  const response = await post<{ organization: ApiOrganization }>(
    `/v2/auth/me/profiles`,
    {
      ...allArgs,
      address: {
        ...allArgs.address,
        country: allArgs.address?.country?.[0]?.value,
      },
      socialMedia: allArgs.socialMedia
        ? mergeDefaultSocialMedia(allArgs.socialMedia)
        : [],
    },
  );
  return mapOrganizationFromApi(response?.data?.organization);
}

export async function confirmUserByVerificationCode({
  code,
  email,
}: ConfirmUserByVerificationCodeArgs) {
  // Through `query`, not interpolated: an address is user text, and `a+tag@…`
  // written straight into a query string reaches Express as `a tag@…` — the
  // account would simply never be found.
  await get<BaseApiResponse<void>>('/v2/auth/confirm', {
    query: { email: email ?? undefined, verificationCode: code ?? undefined },
  });
  return true;
}

export async function fetchUsers(options?: FetchUsersArgs) {
  let isPublic = {};
  let types: OrganizationUserType[] = [];

  const userTypes = new Set(options?.types);

  if (!options?.organizationId) {
    isPublic = {
      isPublic: userTypes.has(OrganizationUserType.CLIENT) ? 'true' : 'false',
    };
  } else {
    if (userTypes.size === 0 || !options.types) {
      types = [OrganizationUserType.CLIENT];
    } else {
      types = options.types;
    }
  }

  const params = {
    ...options?.filters,
    ...isPublic,
    membershipIds: options?.membershipIds?.length
      ? options.membershipIds.join(',')
      : undefined,
    types: types.length ? types.join(',') : undefined,
    withoutTrainer: options?.withoutTrainer ? 'true' : undefined,
  };

  const response = await get<BaseApiPaginatedResponse<ApiUser[]>>(
    `/v2/${options?.organizationId ? `organizations/${options.organizationId}/clients` : 'users'}`,
    { query: params },
  );

  return {
    ...response,
    data: response?.data?.map(mapUserFromApi),
  };
}

export async function fetchPublicAuthors(options?: FetchAuthorsArgs) {
  const response = await postWithPagination<ApiAuthor[]>(
    `/v2/public/authors`,
    options?.filters,
  );
  return {
    ...response,
    data: response?.data?.map(mapAuthorFromApi),
  };
}
export async function fetchMarketplaceAuthors(options?: FetchAuthorsArgs) {
  const response = await postWithPagination<ApiUser[]>(
    `/v2/marketplace/authors`,
    options?.filters,
  );
  return {
    ...response,
    data: response?.data?.map(mapUserFromApi),
  };
}

export async function fetchContentLibraryAuthors(options?: FetchAuthorsArgs) {
  const response = await postWithPagination<ApiUser[]>(
    `/v2/my-library/authors`,
    options?.filters,
  );
  return {
    ...response,
    data: response?.data?.map(mapUserFromApi),
  };
}

export async function fetchOrganizationUsers(
  options: FetchOrganizationUsersArgs,
) {
  const types = options?.types || [];
  const params = {
    ...options.filters,
    types: types.length ? types.join(',') : undefined,
  };
  const response = await get<BaseApiPaginatedResponse<ApiUser[]>>(
    `/v2/organizations/${options?.organizationId}/members`,
    { query: params },
  );
  return {
    ...response,
    data: response?.data?.map(mapUserFromApi),
  };
}

export async function fetchOrganizationUsersByRole(
  options: FetchOrganizationUsersByRoleArgs,
) {
  const roles = options?.roles || [];
  const params = {
    ...options.filters,
    roles: roles.length ? roles.join(',') : undefined,
  };
  const response = await get<BaseApiPaginatedResponse<ApiUser[]>>(
    `/v2/organizations/${options?.organizationId}/users`,
    { query: params },
  );
  return {
    ...response,
    data: response?.data?.map(mapUserFromApi),
  };
}

export async function disconnectOrganizationUserById({
  id,
  allowContentAccess = true,
  organizationId,
}: DisconnectOrganizationUserArgs) {
  await put(
    `/v2/${organizationId ? `organizations/${organizationId}/clients/disconnect` : 'users/disconnect'}/${id}`,
    { allowContentAccess },
  );
  return true;
}

export async function archiveUnarchiveOrganizationUserById({
  id,
  organizationId,
  type,
}: ArchiveUnarchiveOrganizationUserArgs) {
  await put(
    `/v2/${organizationId ? `organizations/${organizationId}/clients` : 'users'}/${type}/${id}`,
  );
  return true;
}
export async function deleteOrganizationUserById({
  id,
  organizationId,
}: DeleteOrganizationUserArgs) {
  await destroy(`/v2/organizations/${organizationId}/users/${id}`);
  return true;
}

export async function deleteAccount() {
  await destroy('/v2/auth/me');
  return true;
}

export async function checkIfUserExistsOnInvite({
  hash,
}: CheckIfUserExistsOnInviteArgs) {
  // The narrow wire shape `check-invite` actually answers — return it as-is,
  // not through `mapUserFromApi`, which manufactures a much broader user
  // shape than this endpoint ever sends.
  const response = await get<BaseApiResponse<ApiInviteCheck>>(
    `/v2/auth/check-invite?hash=${hash}`,
  );
  return response?.data ?? null;
}

export async function acceptUserInvite({
  hash,
  privacyPolicy,
  touVersionId,
  connectionTouVersionId,
}: AcceptUserInviteArgs) {
  const resp = await postWithPagination<ApiUser>(
    `/v2/auth/accept-invite?hash=${hash}`,
    {
      privacyPolicy,
      ...(touVersionId ? { touVersionId } : {}),
      ...(connectionTouVersionId ? { connectionTouVersionId } : {}),
    },
  );
  return mapUserFromApi(resp?.data);
}

export async function resendVerificationCode() {
  await get('/v2/auth/me/resend-verification-code');
  return true;
}

export async function fetchCountries({
  filters,
}: FetchCountriesArgs): Promise<BaseApiPaginatedResponse<UiAddressCountry[]>> {
  const response = await get<BaseApiPaginatedResponse<ApiAddressCountry[]>>(
    '/v2/countries',
    {
      query: filters,
    },
  );
  return {
    ...response,
    data:
      (response?.data
        ?.map(mapAddressCountryFromApi)
        .filter((country) => Boolean(country)) as UiAddressCountry[]) ??
      ([] as UiAddressCountry[]),
    message: response?.message || '',
    status: response?.status || 200,
    total: response?.total || 0,
  };
}

export async function logout() {
  await endSession();
  return true;
}

export async function updateUserRoles({
  id,
  organizationId,
  roleIds,
}: UpdateUserRolesArgs) {
  return patch(
    `/v2/${organizationId ? `organizations/${organizationId}/clients` : 'users'}/${id}`,
    {
      roleIds,
    },
  );
}

export async function updateOrgUser({
  id,
  name,
  organizationId,
  status,
  userSettings,
}: UpdateOrgUserArgs) {
  const response = await patch<ApiUser>(
    `/v2/organizations/${organizationId}/clients/${id}`,
    { name, status, userSettings },
  );
  return mapUserFromApi(response?.data);
}

export async function fetchOrgUserSignedProfileUrl({
  id,
  mediaType,
  organizationId,
}: FetchOrgUserSignedProfileUrlArgs) {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2/organizations/${organizationId}/clients/${id}/signed-upload-profile-url?mediaType=${mediaType}`,
  );
  return response?.data;
}

export async function updateUser(settings: UiUserUpdateArgs) {
  const allSettings = createUpdateUserArgs(settings);

  const response = await patch<ApiUser>(
    '/v2/auth/me',
    omitDeepUndefined(allSettings),
  );
  return mapUserFromApi(response?.data);
}

export async function fetchPublicHealthIssues({
  filters,
  locale,
}: FetchPublicHealthIssuesArgs) {
  const response = await get<BaseApiPaginatedResponse<ApiTag[]>>(
    `/v2/public/health-issues`,
    {
      query: {
        ...filters,
      },
    },
  );
  return {
    ...response,
    data: response?.data?.map((tag) => mapTagFromApi(locale, tag)) as UiTag[],
  };
}

export async function fetchPublicGoals({
  filters,
  locale,
}: FetchPublicHealthIssuesArgs) {
  const response = await get<BaseApiPaginatedResponse<ApiTag[]>>(
    `/v2/public/goals`,
    {
      query: {
        ...filters,
      },
    },
  );
  return {
    ...response,
    data: response?.data?.map((tag) => mapTagFromApi(locale, tag)) as UiTag[],
  };
}
